import React from 'react';

const buildSortMenu = (field) => ({
  type: 'sort',
  field,
  showSortButtonInHeader: false,
  options: [
    {
      label: 'Sort Ascending',
      icon: 'pi pi-arrow-up',
      order: 1,
    },
    {
      label: 'Sort Descending',
      icon: 'pi pi-arrow-down',
      order: -1,
    },
  ],
});

const toFilterOption = (value) => {
  if (value && typeof value === 'object') {
    if (value.props && value.props.children) {
      return toFilterOption(value.props.children);
    }
    const val = String(
      value.value
      ?? value.id
      ?? value.projectId
      ?? value.projectName
      ?? value.name
      ?? value.label
      ?? '',
    ).trim();
    const label = String(
      value.label
      ?? value.projectName
      ?? value.name
      ?? value.value
      ?? '',
    ).trim();
    return {
      id: val || '[object Object]',
      name: label || '[object Object]',
      label: label || '[object Object]',
      value: val || '[object Object]',
    };
  }
  const normalizedValue = String(value ?? '').trim();
  return {
    id: normalizedValue,
    name: normalizedValue,
    label: normalizedValue,
    value: normalizedValue,
  };
};

const buildGlobalFilterMenuItems = (field, header, values = [], sortField, selectedValues = []) => [
  {
    label: header,
    items: [
      buildSortMenu(sortField || field),
      {
        separator: true,
      },
      {
        type: 'filter',
        field,
        label: header,
        template: 'multiSelect',
        selectedValues: selectedValues.map(toFilterOption),
        selectedValue: selectedValues[0] ? toFilterOption(selectedValues[0]) : null,
        selectedLabels: selectedValues.map((val) => toFilterOption(val).label),
        selectedLabel: selectedValues[0] ? toFilterOption(selectedValues[0]).label : '',
        value: selectedValues,
        options: values.map(toFilterOption),
        values: values.map(toFilterOption),
      },
    ],
  },
];

const buildSingleSelectFilterMenuItems = (field, header, values = [], sortField, selectedValues = []) => [
  {
    label: header,
    items: [
      buildSortMenu(sortField || field),
      {
        separator: true,
      },
      {
        type: 'filter',
        field,
        label: header,
        template: 'singleSelect',
        selectedValues: selectedValues.map(toFilterOption),
        selectedValue: selectedValues[0] ? toFilterOption(selectedValues[0]) : null,
        selectedLabels: selectedValues.map((val) => toFilterOption(val).label),
        selectedLabel: selectedValues[0] ? toFilterOption(selectedValues[0]).label : '',
        value: selectedValues[0] || null,
        options: values.map(toFilterOption),
        values: values.map(toFilterOption),
      },
    ],
  },
];

const buildColumn = ({
  field,
  header,
  width,
  order,
  defaultPinned = false,
  sortable = true,
  filter = false,
  isGlobalFilter = false,
  visible = true,
  align,
  className = '',
  headerClassName = '',
  sortField,
  filterField,
  menuItems = [],
  showFilterButtonInHeader = false,
  filterVisible = true,
  ellipsis = false,
}) => ({
  field,
  header,
  width,
  minWidth: width,
  order,
  filter,
  isGlobalFilter,
  visible,
  sortable,
  pinnable: Boolean(defaultPinned),
  defaultPinned,
  showFilterButtonInHeader,
  filterVisible,
  showPinButtonInHeader: false,
  menuItems,
  ellipsis,
  sortField: sortField || field,
  filterField: filterField || field,
  ...(align ? { align, headerAlign: align } : {}),
  ...(className ? { className, bodyClassName: className } : {}),
  ...(headerClassName ? { headerClassName } : {}),
});

const buildDateRangeFilterMenuItems = (field, header, sortField) => [
  {
    label: header,
    items: [
      buildSortMenu(sortField || field),
      {
        separator: true,
      },
      {
        type: 'filter',
        field,
        label: header,
        template: 'dateRange',
        range: [null, null],
      },
    ],
  },
];

/* eslint-disable max-lines-per-function */
export const buildCsatHistoryTableConfig = (
  filterOptions = {},
  defaultProjectName = '',
  showAction = true,
  defaultProjectId = '',
) => {
  const buildFilterColumn = ({
    field,
    header,
    width,
    sortField,
    filterField,
    visible = false,
    defaultPinned = false,
    isDate = false,
    className = '',
    headerClassName = '',
    ellipsis = false,
  }) => buildColumn({
    field,
    header,
    width,
    visible,
    defaultPinned,
    filter: true,
    isGlobalFilter: true,
    sortField,
    filterField,
    className,
    headerClassName,
    ellipsis,
    menuItems: isDate
      ? buildDateRangeFilterMenuItems(filterField || field, header, sortField || field)
      : buildGlobalFilterMenuItems(
        filterField || field,
        header,
        filterOptions?.[filterField || field] || filterOptions?.[field] || [],
        sortField || field,
      ),
  });

  return [
    buildColumn({
      field: 'projectId',
      header: 'Project ID',
      width: 120,
      type: 'string',
      filter: true,
      isGlobalFilter: true,
      // singleSelect (same template as Project Name) so the "Filters applied" preview
      // correctly shows the selected ID value instead of falling back to the column label.
      menuItems: buildSingleSelectFilterMenuItems(
        'projectId',
        'Project ID',
        filterOptions.projectId || [],
        'projectId',
        defaultProjectId ? [defaultProjectId] : [],
      ),
      className: 'col-project-id',
      headerClassName: 'col-project-id',
    }),
    buildColumn({
      field: 'projectName',
      header: 'Project Name',
      width: 180,
      type: 'string',
      filter: true,
      isGlobalFilter: true,
      menuItems: buildSingleSelectFilterMenuItems(
        'projectName',
        'Project Name',
        filterOptions.projectName || [],
        'projectName',
        defaultProjectName ? [defaultProjectName] : [],
      ),
      className: 'col-project-name',
      headerClassName: 'col-project-name',
      ellipsis: true,
    }),
    buildFilterColumn({
      field: 'clientName',
      header: 'Client Name',
      width: 180,
      filterField: 'clientName',
      visible: false,
      className: 'col-client-name',
      headerClassName: 'col-client-name',
      ellipsis: true,
    }),

    buildColumn({
      field: 'projectVertical',
      header: 'Project Vertical',
      width: 140,
      type: 'string',
      filter: true,
      isGlobalFilter: true,
      menuItems: buildGlobalFilterMenuItems(
        'projectVertical',
        'Project Vertical',
        filterOptions.projectVertical || [],
        'projectVertical',
      ),
      className: 'col-project-vertical',
      headerClassName: 'col-project-vertical',
    }),
    buildFilterColumn({
      field: 'projectType',
      header: 'Project Type',
      width: 180,
      filterField: 'projectType',
    }),
    buildFilterColumn({
      field: 'projectStatus',
      header: 'Project Status',
      width: 180,
      filterField: 'projectStatus',
    }),
    buildFilterColumn({
      field: 'projectManager',
      header: 'Project Manager',
      width: 180,
      filterField: 'projectManager',
    }),
    buildFilterColumn({
      field: 'baselineStartDate',
      header: 'Baseline Start Date',
      width: 180,
      filterField: 'baselineStartDate',
      isDate: true,
    }),
    buildFilterColumn({
      field: 'baselineEndDate',
      header: 'Baseline End Date',
      width: 180,
      filterField: 'baselineEndDate',
      isDate: true,
    }),
    buildFilterColumn({
      field: 'actualStartDate',
      header: 'Actual Start Date',
      width: 180,
      filterField: 'actualStartDate',
      isDate: true,
    }),
    buildFilterColumn({
      field: 'replannedEndDate',
      header: 'Replanned End Date',
      width: 180,
      filterField: 'replannedEndDate',
      isDate: true,
    }),
    buildFilterColumn({
      field: 'activeDate',
      header: 'Active Date',
      width: 180,
      filterField: 'activeDate',
      isDate: true,
    }),
    buildColumn({
      field: 'receivedDate',
      header: 'CSAT Received Date',
      width: 160,
      type: 'string',
      filter: true,
      isGlobalFilter: true,
      menuItems: buildDateRangeFilterMenuItems(
        'receivedDate',
        'CSAT Received Date',
        'receivedDate',
      ),
      className: 'hide-on-mobile',
      headerClassName: 'hide-on-mobile',
    }),
    buildFilterColumn({
      field: 'csatNotReceivedDate',
      header: 'CSAT Not Received Date',
      width: 180,
      filterField: 'csatNotReceivedDate',
      isDate: true,
    }),
    buildColumn({
      field: 'serviceQuality',
      header: (
        <div className="multiline-header">
          Quality of Service
          <br />
          from Niveus
        </div>
      ),
      width: 220,
      type: 'number',
      align: 'right',
      className: 'csat-number-column hide-on-mobile',
      headerClassName: 'csat-number-column hide-on-mobile',
    }),
    buildColumn({
      field: 'roi',
      header: (
        <div className="multiline-header">
          Return-On-Investment
          <br />
          on Project
        </div>
      ),
      width: 220,
      type: 'number',
      align: 'right',
      className: 'csat-number-column hide-on-mobile',
      headerClassName: 'csat-number-column hide-on-mobile',
    }),
    buildColumn({
      field: 'management',
      header: (
        <div className="multiline-header">
          Project Management &
          <br />
          Communication on Project
        </div>
      ),
      width: 300,
      type: 'number',
      align: 'right',
      className: 'csat-number-column hide-on-mobile',
      headerClassName: 'csat-number-column hide-on-mobile',
    }),
    buildColumn({
      field: 'overall',
      header: (
        <div className="multiline-header">
          Overall
          <br />
          Experience
        </div>
      ),
      width: 160,
      type: 'number',
      align: 'right',
      className: 'csat-number-column hide-on-mobile',
      headerClassName: 'csat-number-column hide-on-mobile',
    }),
    buildColumn({
      field: 'customerName',
      header: 'Customer Name',
      width: 180,
      type: 'string',
      visible: true,
      className: 'col-customer-name',
      headerClassName: 'col-customer-name',
      ellipsis: true,
    }),
    buildColumn({
      field: 'feedback',
      header: 'Other Feedback',
      width: 250,
      type: 'string',
      visible: true,
      className: 'hide-on-mobile',
      headerClassName: 'hide-on-mobile',
      ellipsis: true,
    }),
    ...(showAction ? [
      buildColumn({
        field: 'action',
        header: 'Action',
        width: 100,
        sortable: false,
        exportable: false,
        menuItems: [],
        visible: true,
        className: 'col-action',
        headerClassName: 'col-action',
      }),
    ] : []),
  ];
};
